import { Icon, IconsEnum } from 'components/Icons';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';

import { Modal } from '../components/modal';
import { RadioButton } from 'components/inputs/radioButton';
import { ReactPortal } from 'components/reactPortal';
import { UnRegisterModalBody } from './components/UnRegisterModalBody';
import { UnRegisterModalProps } from '.';
import { breakpoint } from 'utility/constant';
import { resetSignUp } from 'features/signup/signup';
import { setIsOpenUnregistrationModal } from 'features/modal/modalSlice';
import styles from './UnRegisterModal.module.scss';
import { usePostFeedUnregistrationMutation } from 'features/api/userSlice';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export const UnRegisterModal = ({ isOpenModal, contentModal }: UnRegisterModalProps) => {
  const dispatch = useAppDispatch();
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [sendFeedback, response] = usePostFeedUnregistrationMutation();
  const { isOpenUnregistrationModal } = useSelector((store: RootState) => store.modal);

  const { doCheck } = useWindowSize();
  const [isVertical] = doCheck([{ comparer: enComparer.less | enComparer.equal, size: breakpoint.md }]);

  const schema = z.object({
    feedbackUnregistration: z.string().min(1),
  });

  type SchemaValidationType = z.infer<typeof schema>;
  const { control, handleSubmit, watch, reset } = useForm<SchemaValidationType>({
    resolver: zodResolver(schema),
  });

  const submitWholeForm: SubmitHandler<SchemaValidationType> = async (data) => {
    await sendFeedback(data.feedbackUnregistration);
    localStorage.removeItem(REGISTRATION_STEP_KEY);
    dispatch(resetSignUp());
  };

  const { push } = useRouter();
  useEffect(() => {
    if (response.isSuccess) {
      push('/');
      setTimeout(() => {
        dispatch(setIsOpenUnregistrationModal(false));
        reset({ feedbackUnregistration: ' ' });
      }, 1000);
    }
  }, [response, dispatch, reset, push]);

  useEffect(() => {
    if (watch().feedbackUnregistration) {
      setIsDisable(false);
    }
  }, [watch, watch().feedbackUnregistration]);

  useEffect(() => {
    if (isOpenUnregistrationModal === false) {
      reset({ feedbackUnregistration: ' ' });
    }
  }, [isOpenUnregistrationModal]);
  if (!isOpenModal) return null;
  return (
    <ReactPortal wrapperId="unRegisterModal">
      <Modal modalType="secondary" modalCategory="unregistration-modal">
        <UnRegisterModalBody {...contentModal}>
          <Icon iconId={IconsEnum.ROUND_QUESTION} color="var(--color-accent)" className={styles.iconQuestion} />
          <h3 className={styles.title}>{contentModal.title}</h3>
          <p className={styles.description}>{contentModal.description}</p>
          <span className={styles.labelReason}>{contentModal.labelUnRegistration}</span>
          <form className={styles.form} onSubmit={handleSubmit(submitWholeForm)}>
            <RadioButton
              name="feedbackUnregistration"
              options={contentModal.reasonUnregistration}
              control={control}
              className={styles.radioButtonMargin}
              isVertical={isVertical}
            ></RadioButton>

            <div className={styles.buttonContainer}>
              <button
                className={styles.buttonContinueRegistration}
                type="button"
                onClick={() => {
                  dispatch(setIsOpenUnregistrationModal(false));
                  reset({ feedbackUnregistration: ' ' });
                }}
              >
                {contentModal.buttonContinueRegistration}
              </button>
              <button className={styles.buttonUnregistration} disabled={isDisable} type="submit">
                {contentModal.buttonUnRegistration}
              </button>
            </div>
          </form>
        </UnRegisterModalBody>
      </Modal>
    </ReactPortal>
  );
};
